import React from 'react';
import { EventAction, UpcomingEvent, eventListedUntil, eventStart } from './UpcomingEventRow';
import { getClubDateParts } from '../utils/clubTime';

/**
 * Slim strip under the hero showing only the next competition (#102).
 * Two lines only (#119): the title already carries the times, so the
 * "Fishing 7am–2pm · location" line lives in Upcoming Events, not here.
 *
 * Everything is reused from Upcoming Events (#105): the club-time formatter
 * (#111), the shared window helpers via getEventRowAction, the .ics download,
 * and the parent's per-minute / tab-focus clock. During fishing hours the
 * action is "Submit a catch" rather than the list's "Fishing now" status.
 */

/** The next competition still on (not yet ended) at `now`, or null. */
export function pickNextCompetition(events: UpcomingEvent[], now: number): UpcomingEvent | null {
  const upcoming = events
    .filter((e) => e.eventType === 'COMPETITION')
    .filter((e) => eventListedUntil(e) >= now)
    .sort((a, b) => new Date(eventStart(a)).getTime() - new Date(eventStart(b)).getTime());
  return upcoming[0] ?? null;
}

interface NextEventStripProps {
  events: UpcomingEvent[];
  now: number;
}

const NextEventStrip: React.FC<NextEventStripProps> = ({ events, now }) => {
  const event = pickNextCompetition(events, now);
  if (!event) return null;
  const parts = getClubDateParts(eventStart(event));

  return (
    <section
      aria-label={`Next competition: ${event.title}`}
      data-testid="next-event-strip"
      className="bg-white border-b border-blue-100 shadow-sm px-4 py-3"
    >
      <div className="max-w-6xl mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            role="img"
            aria-label={parts?.fullDate ?? ''}
            className="flex w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-fishing-blue py-1.5 text-center text-white whitespace-nowrap"
          >
            <span aria-hidden="true" className="text-[11px] font-bold tracking-widest leading-tight">{parts?.monthShort}</span>
            <span aria-hidden="true" className="text-xl font-extrabold leading-none">{parts?.day}</span>
            <span aria-hidden="true" className="text-[11px] font-medium leading-tight">{parts?.weekdayShort}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-fishing-blue">Next competition</p>
            <p data-testid="next-event-title" title={event.title} className="truncate font-semibold text-gray-900">
              {event.title}
            </p>
          </div>
        </div>
        <div className="sm:shrink-0">
          <EventAction event={event} now={now} catchDuringFishing />
        </div>
      </div>
    </section>
  );
};

export default NextEventStrip;
